/**
 * CL-CART-01 — read a line item's quantity as a number, safely.
 *
 * A line item's `quantity` is NOT a number. `src/util/api.js` installs transit type handlers that
 * turn the SDK's BigDecimal into a decimal.js `Decimal`, whose `toString()` gives "3". But a
 * BigDecimal that has NOT been through those handlers stringifies to "[object Object]", and
 * `Number("[object Object]")` is `NaN` — which reads as "no quantity" and silently becomes 1.
 *
 * A wrong quantity is not a crash, which is what makes it worth a helper: the row simply prints
 * the wrong number of items, or drops the "× 2" entirely, and nothing anywhere says so.
 *
 * This was documented in `orderItemFromListing` and then not applied in `LineItemCartItemsMaybe`,
 * which read `Number(li.quantity)` directly — one file warning about a trap the file beside it
 * walked into. Found in review. Shared so the next reader cannot get two answers.
 *
 * @param {*} raw - `lineItem.quantity`: a Decimal, a BigDecimal, a number, a string, or absent
 * @returns {number} the quantity, defaulting to 1 when it cannot be read
 */
export const lineItemQuantity = raw => {
  if (raw == null) {
    return 1;
  }
  // A decimal.js Decimal stringifies correctly; an untransformed BigDecimal carries its digits on
  // `.value`, so prefer that over a toString() that would give "[object Object]".
  const text = typeof raw === 'object' && raw.value != null ? raw.value : raw.toString();
  return Number(text) || 1;
};
